/**
 * Message Bubble Component
 */

import { formatRelativeTime } from './utils/format-utils.js';
import { createAssistantMark } from './utils/assistant-mark.js';
import { createAssistantMessageBody } from './rendering/assistant-message-body.js';
import { hasAssistantTextContent } from './rendering/message-content-utils.js';
import { createMessageFeedback } from './message-feedback.js';
import { t } from '../../i18n.js';

/**
 * If this user message resolves a preceding entity_choice, show the option label.
 * @param {Array} messages
 * @param {number} idx
 * @returns {string|undefined}
 */
export function resolveUserDisplayContent(messages, idx) {
  const msg = messages[idx];
  if (msg.role !== 'user' || idx === 0) return undefined;

  const prev = messages[idx - 1];
  if (prev.role !== 'assistant' || !prev.parts) return undefined;

  for (const part of prev.parts) {
    if (part.kind !== 'block' || part.block?.type !== 'entity_choice') continue;
    const opts = part.block.options || [];
    const match = opts.find((o) => (o.value ?? o.label) === msg.content);
    if (match) return match.label;
  }

  return undefined;
}

function buildAssistantBody(message, renderContext) {
  const {
    isStreaming = false,
    showThinking = false,
    allMessages = [],
    onChoiceSelect,
    listSelection,
  } = renderContext;

  return createAssistantMessageBody({
    message,
    isStreaming,
    showThinking,
    allMessages,
    onChoiceSelect,
    listSelection,
  });
}

/**
 * @param {Object} message
 * @param {Object} [renderContext]
 * @returns {HTMLElement}
 */
export function createMessageBubble(message, renderContext = {}) {
  const { role, content, createdAt, isOptimistic, isError, streamError } = message;
  const isUser = role === 'user';
  const {
    onRetry,
    onFeedback,
    feedbackRating,
    displayContent,
    isStreaming = false,
  } = renderContext;

  const container = document.createElement('div');
  container.className = `ai-message-container ${isUser ? 'user' : 'assistant'}`;
  container.dataset.messageId = message._id;

  const messageWrapper = document.createElement('div');
  messageWrapper.className = 'ai-message-wrapper';

  const bubble = document.createElement('div');
  bubble.className = `ai-message-bubble ${isUser ? 'user' : 'assistant'}${isOptimistic ? ' optimistic' : ''}${isError ? ' error' : ''}`;

  if (isUser) {
    const contentDiv = document.createElement('div');
    contentDiv.className = 'ai-message-content';
    contentDiv.textContent = displayContent ?? message.displayContent ?? content;
    contentDiv.style.whiteSpace = 'pre-wrap';
    bubble.appendChild(contentDiv);
    messageWrapper.appendChild(bubble);
  } else if (isError) {
    const errorEl = document.createElement('div');
    errorEl.className = 'ai-message-error';
    errorEl.setAttribute('role', 'alert');
    errorEl.textContent = content;

    if (streamError?.retriable && onRetry) {
      const retryBtn = document.createElement('button');
      retryBtn.type = 'button';
      retryBtn.className = 'ai-retry-btn';
      retryBtn.textContent = t('ai_retry');
      retryBtn.onclick = onRetry;
      errorEl.appendChild(document.createElement('br'));
      errorEl.appendChild(retryBtn);
    }

    bubble.appendChild(errorEl);
    messageWrapper.appendChild(bubble);
  } else {
    const contentDiv = document.createElement('div');
    contentDiv.className = 'ai-message-content';
    contentDiv.appendChild(buildAssistantBody(message, renderContext));
    bubble.appendChild(contentDiv);
    messageWrapper.appendChild(bubble);

    if (!isStreaming && onFeedback) {
      messageWrapper.appendChild(
        createMessageFeedback({
          messageId: message._id,
          rating: feedbackRating ?? null,
          onSubmit: onFeedback,
        })
      );
    }
  }

  const timestamp = document.createElement('div');
  timestamp.className = 'ai-message-time';
  timestamp.textContent = formatRelativeTime(createdAt);
  messageWrapper.appendChild(timestamp);

  if (isUser) {
    container.appendChild(messageWrapper);
  } else {
    container.appendChild(createAssistantMark({ size: 14, className: 'ai-mark ai-mark--sm' }));
    container.appendChild(messageWrapper);
  }

  return container;
}

/**
 * Update only the assistant message body (for streaming — avoids full list re-render).
 * @param {string} messageId
 * @param {Object} message
 * @param {Object} renderContext
 * @param {HTMLElement} [messagesContainer]
 * @returns {boolean} Whether the bubble was found and updated
 */
export function updateAssistantMessageBubble(messageId, message, renderContext, messagesContainer) {
  const root = messagesContainer || document.getElementById('ai-messages-container');
  if (!root) return false;

  const bubbleEl = root.querySelector(`[data-message-id="${messageId}"]`);
  if (!bubbleEl) return false;

  const contentDiv = bubbleEl.querySelector('.ai-message-content');
  if (!contentDiv) return false;

  const body = buildAssistantBody(message, renderContext);
  while (contentDiv.firstChild) {
    contentDiv.removeChild(contentDiv.firstChild);
  }
  contentDiv.appendChild(body);
  return true;
}

export { hasAssistantTextContent };
